import { ArrowRight, Bot, MessageSquareText } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { CoogoStudioHomeRenewalCopy } from '../coogo-studio-home-renewal-copy'

interface HomeWorkContextProps {
  copy: CoogoStudioHomeRenewalCopy
  projectName: string | null
  onOpenBot: () => void
  onOpenProjectChat: () => void
}

export function HomeWorkContext({
  copy,
  projectName,
  onOpenBot,
  onOpenProjectChat,
}: HomeWorkContextProps) {
  return (
    <section
      aria-labelledby="home-work-context-title"
      data-testid="home-work-context"
      className="mt-5"
    >
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <h2
          id="home-work-context-title"
          className="text-sm font-semibold text-foreground"
        >
          {copy.contextTitle}
        </h2>
        <p className="max-w-[520px] text-xs leading-5 text-muted-foreground">
          {copy.contextHint}
        </p>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <Card variant="outline" className="min-w-0 bg-card">
          <CardContent className="flex h-full items-start gap-3 p-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Bot className="h-4 w-4" aria-hidden />
            </span>
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <h3 className="text-sm font-semibold text-foreground">
                {copy.botContextTitle}
              </h3>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {copy.botContextDetail}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onOpenBot}
                className="-ml-3 mt-2 text-primary"
              >
                {copy.botContextAction}
                <ArrowRight aria-hidden />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card
          variant="outline"
          className={projectName ? 'min-w-0 bg-card' : 'min-w-0 bg-muted/30'}
          data-testid="home-project-chat-context"
          aria-disabled={projectName ? undefined : true}
        >
          <CardContent className="flex h-full items-start gap-3 p-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
              <MessageSquareText className="h-4 w-4" aria-hidden />
            </span>
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <h3 className="text-sm font-semibold text-foreground">
                {copy.projectChatContextTitle}
              </h3>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {projectName
                  ? copy.projectChatContextDetail.replace(
                      '{project}',
                      () => projectName,
                    )
                  : copy.projectChatContextUnavailable}
              </p>
              {projectName ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onOpenProjectChat}
                  className="-ml-3 mt-2 text-primary"
                >
                  {copy.projectChatContextAction}
                  <ArrowRight aria-hidden />
                </Button>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  )
}
